import React, { type ReactNode } from 'react';

import { FormattedMessage, type MessageDescriptor } from 'react-intl';

import Button, { type ButtonProps } from '@atlaskit/button/standard-button';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import { Text } from '@atlaskit/primitives/compiled/text';

import { PfLink } from '../PfLink';
import { ErrorSection } from './ErrorSection';
import { IntlNoticeMessages, NoticeMessage } from './NoticeMessages.intl';

interface NoticeProps {
	title: MessageDescriptor;
	titleVars?: { [varName: string]: string };
	message: MessageDescriptor;
	messageVars?: { [varName: string]: string };
	confirmText: MessageDescriptor;
	confirmLink?: string;
	handleClose: (confirmed?: boolean) => void;
	isErrorNotice?: boolean;
	errorDetails?: string;
	isWarningNotice?: boolean;
	// Used in server to force our notices to pop over the Issue Forms dialog
	overrideZIndex?: boolean;
}

export const NoticeDialog: React.FC<NoticeProps> = ({
	title,
	titleVars,
	message,
	messageVars,
	confirmText,
	confirmLink,
	handleClose,
	isErrorNotice,
	errorDetails,
	isWarningNotice,
	overrideZIndex,
}) => {
	const appearance = isErrorNotice ? 'danger' : isWarningNotice ? 'warning' : undefined;

	const actions: Array<ButtonProps & { text: ReactNode }> = [];
	if (isErrorNotice) {
		// No unconfirmed close button.
	} else if (confirmLink) {
		actions.push({
			text: <FormattedMessage {...IntlNoticeMessages[NoticeMessage.Close]} />,
			onClick: (): void => handleClose(false),
			appearance: 'subtle',
		});
	} else {
		actions.push({
			text: <FormattedMessage {...IntlNoticeMessages[NoticeMessage.Cancel]} />,
			onClick: (): void => handleClose(false),
			appearance: 'subtle',
		});
	}

	const primaryButtonAppearance = appearance || 'primary';
	if (confirmLink) {
		actions.push({
			text: <FormattedMessage {...confirmText} />,
			href: confirmLink,
			target: '_blank',
			rel: 'noopener noreferrer',
			appearance: primaryButtonAppearance,
		});
	} else {
		actions.push({
			text: <FormattedMessage {...confirmText} />,
			onClick: (): void => handleClose(true),
			appearance: primaryButtonAppearance,
		});
	}

	let onOpenCompleteF: ((node: HTMLElement, isAppearing: boolean) => void) | undefined;
	if (overrideZIndex) {
		onOpenCompleteF = (node, isAppearing) => {
			// oxlint-disable-next-line no-param-reassign, no-unused-expressions
			overrideZIndex && node.parentElement ? (node.parentElement.style.zIndex = '5000') : undefined;
		};
	}

	return (
		<ModalDialog
			onOpenComplete={onOpenCompleteF}
			onClose={() => handleClose()}
			shouldCloseOnOverlayClick={false}
		>
			<ModalHeader hasCloseButton>
				<ModalTitle appearance={appearance}>
					{<FormattedMessage {...title} values={titleVars} />}
				</ModalTitle>
			</ModalHeader>

			<ModalBody>
				<div>{<FormattedMessage {...message} values={messageVars} />}</div>
				{isErrorNotice && (
					<Text as="p">
						<FormattedMessage {...IntlNoticeMessages[NoticeMessage.ErrorExplanations1]} />{' '}
						<PfLink href="http://links.thinktilt.net/error-explanations">
							<FormattedMessage {...IntlNoticeMessages[NoticeMessage.ErrorExplanations2]} />
						</PfLink>{' '}
						<FormattedMessage {...IntlNoticeMessages[NoticeMessage.ErrorExplanations3]} />{' '}
						<PfLink href="https://support.thinktilt.com/">
							<FormattedMessage {...IntlNoticeMessages[NoticeMessage.ErrorExplanations4]} />
						</PfLink>
						.
					</Text>
				)}
				{!!errorDetails && <ErrorSection errorDetails={errorDetails} />}
			</ModalBody>
			<ModalFooter>
				{actions.map(({ text, ...props }) => (
					<Button {...props}>{text}</Button>
				))}
			</ModalFooter>
		</ModalDialog>
	);
};
